import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';
import CatalogItem from '../../../models/helperTypes/CatalogItem';
import BaseInputWrapper from '../BaseInputWrapper';
import InputProps from '../InputProps';
import StyledSlider from '../helpers/StyledSlider';

const useStyles = makeStyles({
  tickLabelContainer: {
    position: 'relative',
    width: '100%',
    height: 18,
    marginTop: 12,
  },
  tickLabel: {
    maxWidth: 90,
  },
});

export interface Props extends InputProps<string, CatalogItem> {
  options: CatalogItem[];
  labelType?: 'all' | 'ends';
}

const Slider = ({
  inputName,
  onChange,
  options,
  defaultValue = '',
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  labelType = 'ends',
}: Props): JSX.Element => {
  const classes = useStyles();
  const inputId = `slider-field-${inputName}`;
  const stepQuantity = options.length;

  // Disable this check on the next line, since we don't actually care what
  // the type of _event actually is. (Note: this mirrors the example given in
  // material-ui's official documentation)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (_event: any, newValue: number | number[]): void => {
    if (typeof newValue === 'number') {
      onChange(options[newValue]);
    }
  };

  function getOffsetForValue(value: string): number {
    let numberOffset = 0;
    options.forEach((item: CatalogItem): void => {
      if (item.Value === value) {
        numberOffset = options.indexOf(item);
      }
    });
    return numberOffset;
  }

  function transformForIndex(index: number): string {
    if (index === 0) {
      return 'translateY(-50%)';
    }
    if (index === options.length - 1) {
      return 'translateX(-100%) translateY(-50%)';
    }
    return 'translateX(-50%) translateY(-50%)';
  }

  function leftForIndex(index: number): number {
    return index <= 0 ? 0 : (index / (options.length - 1)) * 100;
  }

  function wordWrapForOption(option: CatalogItem): string {
    return typeof option.Text === 'string' && option.Text.length < 15
      ? 'nowrap'
      : 'normal';
  }

  function tickLabelShouldShow(option: CatalogItem): boolean {
    if (labelType === 'all') {
      return true;
    }
    return (
      options.indexOf(option) === 0 ||
      options.indexOf(option) === options.length - 1
    );
  }

  function generateCssForLabelTick(option: CatalogItem): {
    [key: string]: string;
  } {
    const itemIndex = options.indexOf(option);
    return {
      position: 'absolute',
      left: `${leftForIndex(itemIndex)}%`,
      transform: transformForIndex(itemIndex),
      whiteSpace: wordWrapForOption(option),
    };
  }

  return (
    <BaseInputWrapper
      inputId={inputId}
      label=""
      error={inputError}
      errorMessage={inputErrorMessage}
    >
      <Grid container>
        <Grid item xs={12}>
          <StyledSlider
            id={inputId}
            name={inputName}
            value={getOffsetForValue(defaultValue)}
            onChange={handleChange}
            step={1}
            marks
            min={0}
            max={stepQuantity - 1}
            disabled={isDisabled}
          />
        </Grid>
        <Grid item xs={12} className={classes.tickLabelContainer}>
          {options.map(
            (curOption: CatalogItem): React.ReactNode => (
              <Typography
                variant="caption"
                data-testid={`slider-option-${curOption.Value}`}
                key={curOption.Value}
                className={classes.tickLabel}
                style={generateCssForLabelTick(curOption)}
              >
                {tickLabelShouldShow(curOption) && curOption.Text}
              </Typography>
            ),
          )}
        </Grid>
      </Grid>
    </BaseInputWrapper>
  );
};

export default Slider;
